import type { CSSProperties } from "react";
import type { TourDocument } from "@heroncendre/schema";
import { pickText, resolveAssetUrl, useCoarsePointer, useTourState } from "@heroncendre/player";
import { accueil } from "./choix.js";
import { useRetourAccueil } from "./useRetourAccueil.js";
import habillage from "./habillage.json";
import { ENTETE_HAUTEUR, ENTETE_LOGO, ENTETE_LOGO_ORDINATEUR } from "./styles.js";

/**
 * ============================================================================
 *  Bandeau.tsx — L'en-tête de la visite, au logo du château
 * ----------------------------------------------------------------------------
 *  Première pièce de l'habillage : un bandeau en HAUT, sur toute la largeur,
 *  logo au centre. Il était au pied de la visite ; il est monté pour libérer le
 *  bas de l'écran — là où tombe le pouce sur un téléphone — et pour coiffer la
 *  visite comme l'en-tête d'un site coiffe ses pages.
 *
 *  Un DÉGRADÉ et non un aplat : sombre au bord haut, il se fond dans le
 *  panorama, qui reste visible jusqu'au bord. Il assombrit juste ce qu'il faut
 *  pour que le logo — blanc — se lise sur un ciel, et il porte aussi le menu
 *  (à gauche) et le choix de langue (à droite), posés par-dessus lui.
 *
 *  Le bandeau ne capte AUCUN geste (`pointerEvents: none`) : on tourne la vue à
 *  travers lui. Seul le logo répond au clic — il ramène à l'accueil, comme le
 *  blason d'un site ramène à son accueil. Sa zone de clic est celle de l'image,
 *  pas du bandeau.
 *
 *  L'image vient de `src/habillage.json`, écrit par `pnpm habillage` : le
 *  fichier lui-même est sur R2 (et en local dans `public/`), jamais dans Git.
 * ==========================================================================*/

export interface BandeauProps {
  tour: TourDocument;
  assetBaseUrl: string;
}

export function Bandeau({ tour, assetBaseUrl, onAccueil }: BandeauProps & { onAccueil: () => void }) {
  // Pendant un parcours, le bandeau s'efface : la bande noire du guide (`guide/`) occupe le haut.
  const enParcours = useTourState((s) => s.guided.state !== "idle");
  const language = useTourState((s) => s.context.language);
  const retour = useRetourAccueil(onAccueil);
  const mobile = useCoarsePointer();
  if (enParcours) return null;

  const titre = pickText(accueil.logoTitre, language);

  return (
    <div style={bandeauStyle}>
      <button
        type="button"
        title={titre}
        aria-label={titre}
        // Le même geste que le bouton « Accueil » (`useRetourAccueil`).
        onClick={retour}
        style={boutonLogo}
      >
        <Logo
          tour={tour}
          assetBaseUrl={assetBaseUrl}
          style={{ ...logoStyle, height: mobile ? ENTETE_LOGO : ENTETE_LOGO_ORDINATEUR }}
        />
      </button>
    </div>
  );
}

/** Le logo seul, sans habillage : l'image, à la hauteur qu'on lui donne. */
export function Logo({
  tour,
  assetBaseUrl,
  style,
}: BandeauProps & { style?: CSSProperties }) {
  // Le nom du lieu en texte alternatif : le logo porte ce nom, et un lecteur
  // d'écran doit pouvoir le dire.
  const nom = Object.values(tour.meta.title)[0] ?? "";

  return (
    // Résolu comme les tuiles : `public/` en local, R2 sous `?live` et en ligne.
    <img src={resolveAssetUrl(assetBaseUrl, habillage.logo)} alt={nom} style={{ ...logoBase, ...style }} />
  );
}

const logoBase: CSSProperties = {
  width: "auto",
  userSelect: "none",
  pointerEvents: "none",
};

const bandeauStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  height: ENTETE_HAUTEUR,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: 10,
  boxSizing: "border-box",
  background: "linear-gradient(to bottom, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0) 100%)",
  pointerEvents: "none",
};

/** Le logo, rendu cliquable sans que le bouton se voie : ni fond, ni cadre. */
const boutonLogo: CSSProperties = {
  appearance: "none",
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  font: "inherit",
  cursor: "pointer",
  pointerEvents: "auto",
  display: "flex",
};

const logoStyle: CSSProperties = {
  // Le nom du château est écrit sous le blason : en dessous de 60 px de haut,
  // ses deux lignes deviennent illisibles. D'où le plancher de `ENTETE_LOGO`.
  height: ENTETE_LOGO,
  width: "auto",
  userSelect: "none",
};
